import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ymd } from '@/components/ui/PeriodPicker';
import { getPending, listCostCentres, type LedgerOption, type PendingBill, type RefType, type VoucherInput } from '@/api/accountsBooks';
import { ChipTag, DateField, FormFooter, FormTitle, Msgs, Posting, Sec, SmBtn, XBtn } from './voucherFormParts';
import { curBal, dd, inr, r2, type Ref } from './voucherFormat';
import { useSaveVoucher, type FormProps } from './useVoucherForm';

/* General journal laid out like Tally's Journal voucher (F7), as the prototype: Dr / Cr
   lines with separate Debit and Credit columns, Cur Bal under each ledger, Bill-wise
   Details for customer / supplier ledgers, and the difference carried to the next line. */

type Side = 'Dr' | 'Cr';
interface Line { key: number; s: Side; l: number | ''; a: number; refs: Ref[]; cc?: number | '' }
interface State { date: string; narr: string; lines: Line[] }
let seq = 1;
const REF_TYPES: RefType[] = ['Agst Ref', 'New Ref', 'Advance', 'On Account'];

/** Cost centres on a line: expense and income ledgers, as the prototype (Indirect Expenses / Incomes). */
const ccOk = (l?: LedgerOption) => !!l && (!!l.expense || l.group === 'Indirect Incomes');

/** A Cr to a customer or a Dr to a supplier settles their bills; the other side raises one. */
const reduces = (x: Line, led?: LedgerOption) => (led?.role === 'D' && x.s === 'Cr') || (led?.role === 'C' && x.s === 'Dr');

export function JournalForm({ voucher, ledgers, nextNo, onClose, onExpense }: FormProps & { onExpense?: () => void }) {
  const ccQ = useQuery({ queryKey: ['acc-books', 'cost-centres'], queryFn: listCostCentres });
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const cash = ledgers.find((l) => l.kind === 'cash');
  const today = ymd(new Date());

  const [s, setS] = useState<State>(() => (voucher
    ? {
      date: voucher.date, narr: voucher.narration ?? '',
      lines: voucher.lines.map((l) => ({ key: seq++, s: l.side, l: l.ledgerId as number | '', a: l.amount, refs: l.bills.map((b) => ({ t: b.refType, name: b.billNo ?? '', amt: b.amount })), cc: l.costCentreId ?? '' })),
    }
    : { date: today, narr: '', lines: [{ key: seq++, s: 'Dr' as Side, l: '' as number | '', a: 0, refs: [] as Ref[] }, { key: seq++, s: 'Cr' as Side, l: cash?.id ?? ('' as number | ''), a: 0, refs: [] as Ref[] }] }));
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));

  const pend = useQueries({
    queries: s.lines.map((x) => ({
      queryKey: ['acc-books', 'pending', x.l, voucher?.id ?? 0],
      queryFn: () => getPending(x.l as number, voucher?.id),
      enabled: x.l !== '' && !!byId.get(x.l as number)?.role,
    })),
  });
  /** Open bills this line can settle (opposite sign to the line). */
  const openOf = (i: number): PendingBill[] => {
    const x = s.lines[i];
    return (pend[i]?.data?.bills ?? []).filter((b) => (x.s === 'Dr' ? b.pending < 0 : b.pending > 0));
  };
  const led = (x: Line) => (x.l === '' ? undefined : byId.get(x.l));

  /** Bill-wise lines as the prototype fills them: raising side → one New Ref; settling side → oldest bills, rest On Account. */
  const autoRefs = (i: number, x: Line): Ref[] => {
    const L = led(x);
    if (!L?.role || !(x.a > 0)) return [];
    if (!reduces(x, L)) return [{ t: 'New Ref', name: '', amt: x.a }];
    const refs: Ref[] = [];
    let left = x.a;
    for (const b of (pend[i]?.data?.bills ?? []).filter((y) => (x.s === 'Dr' ? y.pending < 0 : y.pending > 0))) {
      if (left <= 0.004) break;
      const a = r2(Math.min(left, Math.abs(b.pending)));
      refs.push({ t: 'Agst Ref', name: b.billNo, amt: a });
      left = r2(left - a);
    }
    if (left > 0.004) refs.push({ t: 'On Account', name: '', amt: left });
    return refs;
  };
  const setLine = (i: number, p: Partial<Line>) => setS((st) => ({
    ...st,
    lines: st.lines.map((x, j) => {
      if (j !== i) return x;
      const y = { ...x, ...p };
      // A new ledger starts with no bills (its own are fetched); a new side or amount refills them.
      return { ...y, refs: 'l' in p ? (byId.get(y.l as number)?.role && y.a > 0 && !reduces(y, byId.get(y.l as number)) ? [{ t: 'New Ref' as const, name: '', amt: y.a }] : []) : autoRefs(i, y) };
    }),
  }));
  const setRefs = (i: number, refs: Ref[]) => setS((st) => ({ ...st, lines: st.lines.map((x, j) => (j === i ? { ...x, refs } : x)) }));
  const setRef = (i: number, j: number, p: Partial<Ref>) => {
    const x = s.lines[i];
    setRefs(i, x.refs.map((r, m) => {
      if (m !== j) return r;
      const y = { ...r, ...p };
      // Picking a bill takes what is pending on it, up to what the line still has to allocate.
      if (p.name !== undefined && y.t === 'Agst Ref') {
        const b = openOf(i).find((z) => z.billNo === p.name);
        const left = r2(x.a - x.refs.filter((_, q) => q !== j).reduce((t, z) => t + z.amt, 0));
        if (b) y.amt = r2(Math.max(0, Math.min(Math.abs(b.pending), left)));
      }
      return y;
    }));
  };

  const dr = r2(s.lines.filter((x) => x.s === 'Dr').reduce((t, x) => t + x.a, 0));
  const cr = r2(s.lines.filter((x) => x.s === 'Cr').reduce((t, x) => t + x.a, 0));
  /** A new line takes the difference on the side that is short, like Tally. */
  const addLine = () => set({ lines: [...s.lines, { key: seq++, s: dr >= cr ? 'Cr' : 'Dr', l: '', a: Math.abs(r2(dr - cr)), refs: [] }] });

  const block: string[] = [];
  if (!s.date) block.push('Enter the date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!(dr > 0)) block.push('Enter the amounts.');
  if (Math.abs(dr - cr) > 0.004) block.push(`Debit and credit don't agree — difference ${inr(r2(dr - cr))}.`);
  if (s.lines.some((x) => x.l === '' && x.a > 0)) block.push('Choose the ledger on every line.');
  const chosen = s.lines.map((x) => x.l).filter((x) => x !== '');
  if (new Set(chosen).size < chosen.length) block.push('The same ledger is on two lines — put it on one line.');
  s.lines.forEach((x, i) => {
    const L = led(x);
    if (!L?.role || !(x.a > 0)) return;
    const alloc = r2(x.refs.reduce((t, r) => t + r.amt, 0)), diff = r2(x.a - alloc);
    if (Math.abs(diff) > 0.004) block.push(`${L.name}: bill-wise details add up to ${inr(alloc)}, not ${inr(x.a)}.`);
    x.refs.forEach((r) => {
      if (r.amt > 0 && r.t === 'Agst Ref' && !r.name) block.push(`${L.name}: pick the bill for the Agst Ref line.`);
      if (r.amt > 0 && (r.t === 'New Ref' || r.t === 'Advance') && !r.name.trim()) block.push(`${L.name}: give the ${r.t} a name.`);
      const b = r.t === 'Agst Ref' && openOf(i).find((y) => y.billNo === r.name);
      if (b && r.amt > Math.abs(b.pending) + 0.004) block.push(`${L.name}: ${r.name} has only ${inr(Math.abs(b.pending))} pending.`);
    });
  });

  const post = s.lines.map((x) => ({
    l: led(x)?.name ?? '', a: x.s === 'Dr' ? x.a : -x.a,
    sub: x.refs.filter((r) => r.amt > 0).map((r) => `${r.t}${r.name ? ` ${r.name}` : ''} · ${inr(r.amt)}`),
  }));
  const build = (): VoucherInput => ({
    date: s.date, narration: s.narr.trim(),
    lines: s.lines.filter((x) => x.l !== '' && x.a > 0).map((x) => ({
      ledgerId: x.l as number, side: x.s, amount: x.a, costCentreId: ccOk(led(x)) && x.cc ? x.cc : null,
      bills: led(x)?.role ? x.refs.filter((r) => r.amt > 0).map((r) => ({ refType: r.t, billNo: r.t === 'On Account' ? null : r.name.trim(), amount: r.amt })) : null,
    })),
  });
  const save = useSaveVoucher('Journal', voucher, onClose);
  const options = ledgers.map((l) => ({ value: l.id, label: l.name, sublabel: l.group }));

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        <FormTitle title="Journal" no={voucher?.voucherNo ?? nextNo} chip={<ChipTag tone="m">General · adjustment</ChipTag>}
          extra={
            <span className="ml-auto inline-flex overflow-hidden rounded-md border border-[var(--border-color)] text-[11px] font-bold">
              <span className="bg-accent/15 px-2.5 py-1 text-accent">General journal</span>
              <button type="button" disabled={!onExpense} onClick={onExpense} className="px-2.5 py-1 text-[var(--text-muted)] hover:text-accent disabled:cursor-not-allowed">Expense bill (GST + TDS)</button>
            </span>
          } />
      }
      footer={
        <FormFooter left={<>Dr <b>{inr(dr)}</b> · Cr <b>{inr(cr)}</b></>} version={voucher ? voucher.version + 1 : undefined}
          saveLabel="Save Journal" blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v })} />
      </div>

      <Sec>Particulars</Sec>
      <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full min-w-[640px] text-xs">
          <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
            <tr><th className="w-[110px] px-2 py-1.5">Dr / Cr</th><th className="px-2 py-1.5">Particulars</th><th className="px-2 py-1.5 text-right">Debit</th><th className="px-2 py-1.5 text-right">Credit</th><th className="w-[36px]" /></tr>
          </thead>
          <tbody>
            {s.lines.map((x, i) => {
              const L = led(x);
              const red = reduces(x, L);
              const ob = openOf(i);
              const alloc = r2(x.refs.reduce((t, r) => t + r.amt, 0)), diff = r2(x.a - alloc);
              const taken = (j: number) => new Set(x.refs.filter((y, m) => m !== j && y.t === 'Agst Ref').map((y) => y.name));
              const amt = (side: Side) => (x.s === side
                ? <Input type="number" step="0.01" min={0} className="ml-auto !w-[140px] text-right font-mono" value={x.a || ''} placeholder="0.00" onChange={(e) => setLine(i, { a: r2(Number(e.target.value) || 0) })} />
                : null);
              return [
                <tr key={x.key} className="border-t border-[var(--border-color)] align-top">
                  <td className="px-2 py-1.5">
                    <Select value={x.s} onChange={(e) => setLine(i, { s: e.target.value as Side })}><option value="Dr">Dr (By)</option><option value="Cr">Cr (To)</option></Select>
                  </td>
                  <td className="px-2 py-1.5">
                    <SearchableSelect options={options} value={x.l} placeholder="Ledger" onChange={(v) => setLine(i, { l: v === '' ? '' : Number(v) })} />
                    <div className="mt-1 text-[11px] text-[var(--text-muted)]">Cur Bal: <b className="text-[var(--text-primary)]">{curBal(L)}</b>{L ? ` · ${L.group}` : ''}</div>
                    {ccOk(L) && (
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]">Cost Centre
                        <Select className="!w-auto" value={x.cc ?? ''} onChange={(e) => setS((st) => ({ ...st, lines: st.lines.map((y, j) => (j === i ? { ...y, cc: e.target.value === '' ? '' : Number(e.target.value) } : y)) }))}>
                          <option value="">— not allocated —</option>
                          {(ccQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Select>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">{amt('Dr')}</td>
                  <td className="px-2 py-1.5 text-right">{amt('Cr')}</td>
                  <td className="px-2 py-1.5 text-right">{s.lines.length > 2 && <XBtn title="Remove line" onClick={() => set({ lines: s.lines.filter((_, j) => j !== i) })} />}</td>
                </tr>,
                L?.role && x.a > 0 ? (
                  <tr key={`${x.key}-bw`}>
                    <td />
                    <td colSpan={4} className="px-2 pb-2">
                      <div className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-secondary)]">
                        Bill-wise Details{' '}
                        <span className="font-semibold normal-case tracking-normal">
                          — Upto {inr(x.a)} {x.s}
                          {Math.abs(diff) > 0.004 && <> · <span className="text-warning">{diff > 0 ? `${inr(diff)} still to allocate` : `${inr(-diff)} over`}</span></>}
                        </span>
                      </div>
                      <table className="w-full text-xs">
                        <thead className="text-left text-[var(--text-muted)]">
                          <tr><th className="w-[130px] py-1">Type of Ref</th><th className="py-1">Name</th><th className="py-1 text-right">Amount</th><th /><th /></tr>
                        </thead>
                        <tbody>
                          {x.refs.map((r, j) => (
                            <tr key={j}>
                              <td className="py-1 pr-2">
                                <Select value={r.t} onChange={(e) => setRef(i, j, { t: e.target.value as RefType, name: '' })}>{REF_TYPES.map((t) => <option key={t}>{t}</option>)}</Select>
                              </td>
                              <td className="py-1 pr-2">
                                {r.t === 'Agst Ref' ? (
                                  <Select value={r.name} onChange={(e) => setRef(i, j, { name: e.target.value })}>
                                    <option value="">— pick bill —</option>
                                    {ob.filter((y) => !taken(j).has(y.billNo)).map((y) => <option key={y.billNo} value={y.billNo}>{y.billNo} · {dd(y.billDate)} · {inr(Math.abs(y.pending))} pending</option>)}
                                  </Select>
                                ) : r.t === 'On Account' ? <span className="text-[var(--text-muted)]">—</span>
                                  : <Input value={r.name} maxLength={60} placeholder={r.t === 'New Ref' ? 'Ref name' : 'e.g. order no.'} onChange={(e) => setRef(i, j, { name: e.target.value })} />}
                              </td>
                              <td className="py-1 text-right">
                                <Input type="number" step="0.01" min={0} className="ml-auto !w-[120px] text-right font-mono" value={r.amt || ''} placeholder="0.00" onChange={(e) => setRef(i, j, { amt: r2(Number(e.target.value) || 0) })} />
                              </td>
                              <td className="px-2 py-1 text-[var(--text-muted)]">{x.s}</td>
                              <td className="py-1"><XBtn title="Remove line" onClick={() => setRefs(i, x.refs.filter((_, m) => m !== j))} /></td>
                            </tr>
                          ))}
                          {x.refs.length === 0 && <tr><td colSpan={5} className="py-1 text-[var(--text-muted)]">No lines — add one.</td></tr>}
                        </tbody>
                      </table>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        <SmBtn onClick={() => setRefs(i, [...x.refs, { t: red ? 'Agst Ref' : 'New Ref', name: '', amt: Math.max(0, diff) }])}>＋ Add line</SmBtn>
                        {red && <SmBtn onClick={() => setRefs(i, autoRefs(i, x))}>⚡ Oldest bills first</SmBtn>}
                      </div>
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-[var(--border-color)] font-bold">
              <td />
              <td className="px-2 py-1.5 text-right">Total {Math.abs(dr - cr) < 0.005 ? <span className="text-success">✓ balanced</span> : <span className="text-warning">Difference {inr(r2(dr - cr))}</span>}</td>
              <td className="px-2 py-1.5 text-right font-mono">{inr(dr)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{inr(cr)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-2">
        <SmBtn onClick={addLine}>＋ Add line{Math.abs(dr - cr) > 0.004 ? ` (${inr(Math.abs(r2(dr - cr)))} ${dr > cr ? 'Cr' : 'Dr'})` : ''}</SmBtn>
      </div>

      <Msgs block={block} />
      <div className="mt-3">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">Narration</span>
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder="Being …" onChange={(e) => set({ narr: e.target.value })} />
      </div>
      <Sec>Posting</Sec>
      <Posting lines={post} />
    </Modal>
  );
}
